import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Divider,
} from '@mui/material';
import {
  getPublicProviders,
  getPublicProviderAvailability,
  createPublicAppointment,
} from '../services/appointmentService';
import { deepPurple } from '@mui/material/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const SERVICES = [
  'E-Scooter Checkout (NPB)',
  'E-Scooter Return (NPB)',
  'E-Scooter Checkout (SP1)',
  'E-Scooter Return (SP1)',
];

/** Shown when a service is selected on step 1 (duration + location). */
const SERVICE_APPOINTMENT_DETAILS = {
  'E-Scooter Checkout (NPB)': {
    summary: 'Duration: 30 Minutes, Location: NPB 2.238',
    lines: [
      'Location:',
      'UTSA Main Campus',
      '1 UTSA Circle, San Antonio, TX 78249',
      'North Paseo Building (NPB)',
      'Room: 2.238-ScooterLab',
    ],
  },
  'E-Scooter Return (NPB)': {
    summary: 'Duration: 15 Minutes',
    lines: [
      'Location:',
      'UTSA Main Campus',
      '1 UTSA Circle, San Antonio, TX 78249',
      'North Paseo Building (NPB)',
      'Room: 2.238-ScooterLab',
    ],
  },
  'E-Scooter Checkout (SP1)': {
    summary: 'Duration: 30 Minutes, Location: SP1-108',
    lines: [
      'Location:',
      'UTSA School of Data Science',
      '506 Dolorosa St, San Antonio, TX 78204',
      'Room: 108-ScooterLab',
    ],
  },
  'E-Scooter Return (SP1)': {
    summary: 'Duration: 15 Minutes, Location: SP1-108',
    lines: [
      'Location:',
      'UTSA School of Data Science',
      '506 Dolorosa St, San Antonio, TX 78204',
      'Room: 108-ScooterLab',
    ],
  },
};

const EMPLOYEE_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const DAYS_HEADER = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getServiceLocationKey = (service) => {
  if (!service) return null;
  if (service.includes('(NPB)')) return 'npb';
  if (service.includes('(SP1)')) return 'sp1';
  return null;
};

export default function BookAppointmentPublic() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    service: '',
    provider: '',
    providerId: null,
    date: '',
    time: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isEmployee: '',
  });

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [providerAvailability, setProviderAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const selectedLocationKey = getServiceLocationKey(formData.service);

  const today = new Date();
  const [calendarMonth, setCalendarMonth] = useState(today.getMonth());
  const [calendarYear, setCalendarYear] = useState(today.getFullYear());

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await getPublicProviders();
        if (result.success) setProviders(result.data || []);
      } catch (e) {
        console.error('Fetch providers:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!formData.providerId || !selectedLocationKey) {
      setProviderAvailability([]);
      return;
    }
    let cancelled = false;
    const fetchAvailability = async () => {
      setLoadingAvailability(true);
      const result = await getPublicProviderAvailability(formData.providerId, selectedLocationKey);
      if (!cancelled) {
        setProviderAvailability(result.success ? (result.data || []) : []);
        setLoadingAvailability(false);
      }
    };
    fetchAvailability();
    return () => { cancelled = true; };
  }, [formData.providerId, selectedLocationKey]);

  const handleChange = (field, value) => {
    setValidationError(null);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const getDayOfWeekFromDate = (dateStr) => {
    if (!dateStr) return null;
    let dateObj;
    if (typeof dateStr === 'string' && dateStr.includes('/')) {
      const [m, d, y] = dateStr.split('/').map(Number);
      dateObj = new Date(y, m - 1, d);
    } else {
      dateObj = new Date(dateStr);
    }
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[dateObj.getDay()];
  };

  const getTimeSlotsForDay = (dayOfWeek) => {
    if (!dayOfWeek || !providerAvailability.length) return [];
    const slotsForDay = providerAvailability.filter(
      (s) => s.day_of_week && String(s.day_of_week).toLowerCase() === dayOfWeek.toLowerCase()
    );
    if (!slotsForDay.length) return [];
    const timeSet = new Set();
    slotsForDay.forEach((slot) => {
      const start = slot.start_time || '';
      const end = slot.end_time || '';
      if (!start || !end) return;
      const startParts = start.split(':').map(Number);
      const endParts = end.split(':').map(Number);
      const sh = startParts[0], sm = startParts[1] || 0;
      const eh = endParts[0], em = endParts[1] || 0;
      const startMins = sh * 60 + sm;
      const endMins = eh * 60 + em;
      if (startMins >= endMins) return;
      for (let m = startMins; m < endMins; m += 15) {
        const h = Math.floor(m / 60);
        const min = m % 60;
        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        const ampm = h < 12 ? 'AM' : 'PM';
        timeSet.add(`${hour12}:${String(min).padStart(2, '0')} ${ampm}`);
      }
    });
    return Array.from(timeSet).sort((a, b) => {
      const parse = (s) => {
        const [h, m] = s.replace(' AM', '').replace(' PM', '').split(':').map(Number);
        return (h === 12 ? 0 : h) * 60 + m + (s.includes('PM') ? 12 * 60 : 0);
      };
      return parse(a) - parse(b);
    });
  };

  const handleNext = () => {
    setValidationError(null);
    if (currentStep === 1) {
      if (!formData.service || !formData.providerId) {
        setValidationError('Please select Service and Provider');
        return;
      }
    } else if (currentStep === 2) {
      if (!formData.date || !formData.time) {
        setValidationError('Please select Date and Time');
        return;
      }
    } else if (currentStep === 3) {
      if (!formData.firstName?.trim() || !formData.lastName?.trim() || !formData.email?.trim() || !formData.isEmployee) {
        setValidationError('Please fill First name, Last name, Email, and UTSA Employee');
        return;
      }
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => setCurrentStep((prev) => prev - 1);

  const handleConfirm = async () => {
    setValidationError(null);
    const missing = [];
    if (!formData.service) missing.push('Service');
    if (!formData.providerId) missing.push('Provider');
    if (!formData.date) missing.push('Date');
    if (!formData.time) missing.push('Time');
    if (!formData.firstName?.trim()) missing.push('First name');
    if (!formData.lastName?.trim()) missing.push('Last name');
    if (!formData.email?.trim()) missing.push('Email');
    if (!formData.isEmployee) missing.push('UTSA Employee');
    if (missing.length > 0) {
      setValidationError(`Please fill in: ${missing.join(', ')}`);
      return;
    }

    let formattedDate = '';
    if (typeof formData.date === 'string' && formData.date.includes('/')) {
      const parts = formData.date.split('/').map((p) => String(p).padStart(2, '0'));
      if (parts.length === 3) formattedDate = `${parts[2]}-${parts[0]}-${parts[1]}`;
    }
    if (!formattedDate) {
      setValidationError('Invalid date');
      return;
    }

    const payload = {
      service: formData.service,
      provider_id: formData.providerId,
      appointment_date: formattedDate,
      appointment_time: formData.time,
      first_name: formData.firstName.trim(),
      last_name: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: (formData.phone || '').trim(),
      is_employee: formData.isEmployee,
    };

    setSubmitting(true);
    try {
      const result = await createPublicAppointment(payload);
      if (result.success) {
        setFormData({
          service: '',
          provider: '',
          providerId: null,
          date: '',
          time: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          isEmployee: '',
        });
        setCurrentStep(5);
      } else {
        setValidationError(result.message || 'Failed to save appointment');
      }
    } catch (err) {
      setValidationError(err.message || 'Failed to save appointment');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePreviousMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear((y) => y - 1);
    } else setCalendarMonth((m) => m - 1);
  };
  const handleNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear((y) => y + 1);
    } else setCalendarMonth((m) => m + 1);
  };

  const firstDay = new Date(calendarYear, calendarMonth, 1);
  const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();
  const calendarDays = [];
  for (let i = 0; i < startingDayOfWeek; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

  const selectedDayOfWeek = formData.date ? getDayOfWeekFromDate(formData.date) : null;
  const timeSlots = selectedDayOfWeek ? getTimeSlotsForDay(selectedDayOfWeek) : [];
  const hasProviderSchedule = providerAvailability.length > 0;
  const noSlotsForDay = formData.date && hasProviderSchedule && timeSlots.length === 0;
  const providerName = formData.provider || 'your provider';
  let selectedDayLabel = '';
  if (formData.date && formData.date.includes('/')) {
    const [m, d, y] = formData.date.split('/').map(Number);
    selectedDayLabel = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  }

  const steps = ['Service & Provider', 'Date & Time', 'Your Info', 'Confirmation'];

  return (
    <>
      <AppBar position="fixed" sx={{ bgcolor: '#4527a0' }}>
        <Toolbar sx={{ minHeight: 56 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            ScooterLab
          </Typography>
          <Typography variant="body2" sx={{ ml: 2, opacity: 0.9 }}>
            Book an appointment
          </Typography>
        </Toolbar>
      </AppBar>
      <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pt: '72px' }}>
        <Container maxWidth="md" sx={{ py: 3, px: 3 }}>
          {/* Header — same as mobile: title + subtitle with gap from nav */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#4527a0', mb: 0.5 }}>
              ScooterLab
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: 16 }}>
              Service Provider
            </Typography>
          </Box>

          {currentStep <= 4 && (
            <Stepper activeStep={currentStep - 1} sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {validationError && currentStep !== 5 && (
            <Alert severity="error" onClose={() => setValidationError(null)} sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          <Paper sx={{ p: 3 }}>
          {currentStep === 1 && (
            <Box>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#4527a0', mb: 1.5 }}>
                Ready to Ride a ScooterLab E-Scooter?
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                If you are a UTSA student looking to rent a scooter from ScooterLab, please read the instructions below
                and continue scrolling to schedule your pickup or return appointment.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                ScooterLab E-Scooter Appointments
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Follow the steps below to book your scooter appointment.
              </Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Which Service Should I Choose?
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2.5, color: 'text.secondary', '& li': { mb: 1 } }}>
                <Typography component="li" variant="body2">
                  <strong>First time borrowing</strong> → choose an <strong>E-Scooter Checkout</strong> option (NPB or SP1—pick the location where you will pick up your scooter).
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Returning or renewing</strong> → choose an <strong>E-Scooter Return</strong> option (NPB or SP1) if you are returning your scooter or extending your loan.
                </Typography>
              </Box>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                What Is a Provider?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.65 }}>
                The Provider is a ScooterLab staff member who will assist you during your appointment. You may choose any
                available provider; all of them can help with checkouts, renewals, and returns. If no dates or times are
                available for a specific provider, please select a different provider to see additional appointment slots.
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>Service &amp; Provider</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="public-appt-service-label" shrink>
                  Service *
                </InputLabel>
                <Select
                  labelId="public-appt-service-label"
                  id="public-appt-service"
                  value={formData.service}
                  label="Service *"
                  displayEmpty
                  renderValue={(selected) =>
                    selected ? selected : <em style={{ color: 'rgba(0,0,0,0.6)' }}>Please Select</em>
                  }
                  onChange={(e) => handleChange('service', e.target.value)}
                >
                  <MenuItem value="" disabled>
                    <em>Please Select</em>
                  </MenuItem>
                  {SERVICES.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="public-appt-provider-label" shrink>
                  Provider *
                </InputLabel>
                <Select
                  labelId="public-appt-provider-label"
                  id="public-appt-provider"
                  value={formData.providerId ?? ''}
                  label="Provider *"
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected === '' || selected == null) {
                      return <em style={{ color: 'rgba(0,0,0,0.6)' }}>Please Select</em>;
                    }
                    const p = providers.find((x) => x.user_id === selected);
                    return p?.name ?? '';
                  }}
                  onChange={(e) => {
                    const p = providers.find((x) => x.user_id === e.target.value);
                    if (p) {
                      handleChange('provider', p.name);
                      handleChange('providerId', p.user_id);
                    }
                  }}
                  disabled={loading}
                >
                  <MenuItem value="" disabled>
                    <em>Please Select</em>
                  </MenuItem>
                  {providers.map((p) => (
                    <MenuItem key={p.user_id} value={p.user_id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {formData.service && SERVICE_APPOINTMENT_DETAILS[formData.service] && (
                <Box
                  sx={{
                    mt: 2,
                    mb: 2,
                    p: 2,
                    bgcolor: deepPurple[50],
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#4527a0' }}>
                    Your appointment details
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {SERVICE_APPOINTMENT_DETAILS[formData.service].summary}
                  </Typography>
                  {SERVICE_APPOINTMENT_DETAILS[formData.service].lines.map((line) => (
                    <Typography key={line} variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {line}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {currentStep === 2 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Select Date</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a date first. Available times for {providerName} will show below.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, p: 1.5, bgcolor: deepPurple[50], borderRadius: 1 }}>
                <Button size="small" onClick={handlePreviousMonth}>‹ Prev</Button>
                <Typography fontWeight="bold">
                  {new Date(calendarYear, calendarMonth).toLocaleString('en-US', { month: 'long' })} {calendarYear}
                </Typography>
                <Button size="small" onClick={handleNextMonth}>Next ›</Button>
              </Box>
              <Grid container spacing={0.5} sx={{ mb: 2 }}>
                {DAYS_HEADER.map((d) => (
                  <Grid item key={d} xs={12/7} sx={{ textAlign: 'center', fontWeight: 600, fontSize: '0.75rem' }}>{d}</Grid>
                ))}
                {calendarDays.map((day, idx) => {
                  const isSelected = day && formData.date === `${calendarMonth + 1}/${day}/${calendarYear}`;
                  const dateObj = day ? new Date(calendarYear, calendarMonth, day) : null;
                  const isPast = dateObj && dateObj < new Date(new Date().setHours(0, 0, 0, 0));
                  return (
                    <Grid item key={idx} xs={12/7}>
                      <Button
                        fullWidth
                        size="small"
                        variant={isSelected ? 'contained' : 'outlined'}
                        disabled={!day || isPast}
                        onClick={() => {
                          if (day && !isPast) {
                            handleChange('date', `${calendarMonth + 1}/${day}/${calendarYear}`);
                            handleChange('time', '');
                          }
                        }}
                        sx={{ minWidth: 36 }}
                      >
                        {day || ''}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
              {formData.date && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {loadingAvailability ? 'Loading…' : `Available times on ${selectedDayLabel}`}
                  </Typography>
                  {loadingAvailability && <CircularProgress size={24} sx={{ mt: 1 }} />}
                  {!loadingAvailability && !hasProviderSchedule && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Provider has no availability for this service location. Please choose another provider.
                    </Typography>
                  )}
                  {!loadingAvailability && hasProviderSchedule && noSlotsForDay && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Provider is not available on this day. Please select another date.
                    </Typography>
                  )}
                  {!loadingAvailability && timeSlots.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {timeSlots.map((t) => (
                        <Button
                          key={t}
                          size="small"
                          variant={formData.time === t ? 'contained' : 'outlined'}
                          onClick={() => handleChange('time', t)}
                        >
                          {t}
                        </Button>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {currentStep === 3 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Your Information</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your name and contact details. This information is not pre-filled.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="First Name *" value={formData.firstName} onChange={(e) => handleChange('firstName', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Last Name *" value={formData.lastName} onChange={(e) => handleChange('lastName', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Email (e.g. my.utsa.edu) *" type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Phone Number" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Are you a UTSA Employee? *</InputLabel>
                    <Select
                      value={formData.isEmployee}
                      label="Are you a UTSA Employee? *"
                      onChange={(e) => handleChange('isEmployee', e.target.value)}
                    >
                      {EMPLOYEE_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}

          {currentStep === 4 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Confirm your appointment</Typography>
              <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="body2"><strong>Service:</strong> {formData.service}</Typography>
                  <Typography variant="body2"><strong>Provider:</strong> {formData.provider}</Typography>
                  <Typography variant="body2"><strong>Date & Time:</strong> {formData.date} at {formData.time}</Typography>
                  <Typography variant="body2"><strong>Name:</strong> {formData.firstName} {formData.lastName}</Typography>
                  <Typography variant="body2"><strong>Email:</strong> {formData.email}</Typography>
                  <Typography variant="body2"><strong>Phone:</strong> {formData.phone || 'Not provided'}</Typography>
                  <Typography variant="body2"><strong>UTSA Employee:</strong> {EMPLOYEE_OPTIONS.find((o) => o.value === formData.isEmployee)?.label}</Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {currentStep === 5 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main' }} />
              <Typography variant="h6" sx={{ mt: 1 }}>Appointment confirmed!</Typography>
              <Typography variant="body2" color="text.secondary">
                Your appointment has been booked. You can book another below if needed.
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            {currentStep === 5 ? (
              <Button variant="contained" onClick={() => setCurrentStep(1)} sx={{ bgcolor: deepPurple[500] }}>
                Book another
              </Button>
            ) : (
              <>
                {currentStep > 1 && (
                  <Button onClick={handleBack}>Back</Button>
                )}
                <Box sx={{ flex: 1 }} />
                {currentStep < 4 ? (
                  <Button variant="contained" onClick={handleNext} sx={{ bgcolor: deepPurple[500] }}>
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={submitting}
                    sx={{ bgcolor: deepPurple[500] }}
                  >
                    {submitting ? <CircularProgress size={24} color="inherit" /> : 'Confirm'}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Container>
      </Box>
    </>
  );
}
